import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { ScreenBrightness } from "@capacitor-community/screen-brightness";
import { useEffect, useRef } from "react";

/**
 * Keeps the screen at full brightness while the ticket viewer is active.
 *
 * @remarks Native platforms only — complete no-op on web/desktop. The original
 * brightness is restored on deactivation, unmount, and while the app sits in
 * the background (re-applied on return to the foreground). `-1` means "system
 * auto" on Android and is restored as such.
 * @param active Whether the viewer is open with at least one ticket.
 */
export const useTicketBrightness = ( active: boolean ): void => {
    const originalRef = useRef<number | null>( null );

    useEffect( () => {
        if ( !active || !Capacitor.isNativePlatform() ) {
            return;
        }

        let cancelled = false;

        const apply = async() => {
            try {
                if ( originalRef.current === null ) {
                    const current = await ScreenBrightness.getBrightness();

                    if ( cancelled ) {
                        return;
                    }

                    originalRef.current = current.brightness;
                }

                await ScreenBrightness.setBrightness( { "brightness": 1 } );
            } catch {
                // Plugin unavailable or permission denied — viewing still works.
            }
        };

        const restore = (): void => {
            const original = originalRef.current;
            originalRef.current = null;

            if ( original !== null ) {
                void ScreenBrightness.setBrightness( { "brightness": original } ).catch( () => undefined );
            }
        };

        void apply();

        const listenerPromise = App.addListener( "appStateChange", ( { isActive } ) => {
            if ( isActive ) {
                void apply();
            } else if ( originalRef.current !== null ) {
                // Restore while backgrounded so the OS shows the user's brightness.
                void ScreenBrightness.setBrightness( { "brightness": originalRef.current } ).catch( () => undefined );
            }
        } );

        return () => {
            cancelled = true;
            void listenerPromise.then( ( handle ) => handle.remove() );
            restore();
        };
    }, [ active ] );
};
